import { Checkbox, Group, Radio } from '@any-ui-react/core';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { EmployeeRole } from '@/common/graphql';
import {
  type EmployeePermissionFormDataType,
  EmployeeRoleLabels,
  IDP_FULL_ACCESS_LABEL,
  isIdpFullAccessApplicableForRole,
} from '@/common/types';

const order: Array<EmployeeRole> = [
  EmployeeRole.Basic,
  EmployeeRole.Advanced,
  EmployeeRole.AdminStandard,
  EmployeeRole.AdminPrivate,
  EmployeeRole.SuperAdmin,
];

const descriptions: Record<EmployeeRole, string> = {
  [EmployeeRole.Basic]: 'Basic users who has limited access.',
  [EmployeeRole.Advanced]: 'Users with access to Reports (e.g. IR and Finance members).',
  [EmployeeRole.AdminStandard]:
    "HR and Corporate Operation users with access to employee's job and personal information of one's assigned company.",
  [EmployeeRole.AdminPrivate]:
    "HR Manager users with access to employee's private and performance review data of one's assigned company.",
  [EmployeeRole.SuperAdmin]: 'Owner of this product.',
};

export const EmployeeRoleRadioGroup = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<EmployeePermissionFormDataType>();
  const value = useWatch({ control, name: 'role' });

  return (
    <>
      <div className="text-md mb-4 font-semibold">Role</div>
      <Controller
        control={control}
        name="role"
        render={({ field: { ...fields } }) => (
          <Radio.Group {...fields} error={errors.role?.message}>
            <Group className="flex flex-wrap flex-col items-start justify-start gap-4 lg:flex-row">
              {order.map(value => (
                <Radio key={value} label={EmployeeRoleLabels[value]} value={value} />
              ))}
            </Group>
          </Radio.Group>
        )}
      />
      <div className="mt-4 grid grid-cols-[24px_1fr] [&_*]:leading-11">
        <MaterialIcons iconName="info" className="text-blue-6" />
        <div className="text-xs">{descriptions[value]}</div>
      </div>
      {isIdpFullAccessApplicableForRole(value) && (
        <div className="mt-4">
          <Controller
            control={control}
            name="idpFullAccess"
            render={({ field: { value, onChange, ...fields } }) => (
              <Checkbox
                {...fields}
                checked={value}
                label={IDP_FULL_ACCESS_LABEL}
                onChange={event => onChange(event.target.checked)}
              />
            )}
          />
        </div>
      )}
    </>
  );
};
